<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { type RouteLocation, type RouteRecordRaw, useRouter } from "vue-router";

import { isIframe } from "@/common/is-iframe";
import InflectionSchemeToolPromotion from "@/components/home/InflectionSchemeToolPromotion.vue";
import SearchBar from "@/components/searchBar/SearchBar.vue";
import SearchBarBanner from "@/components/searchBar/SearchBarBanner.vue";
import { MatomoHelper } from "@/helpers";
import { useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const router = useRouter();
const { activeLanguageGroup } = storeToRefs(useSettingsStore());

const previousRoute = computed<RouteLocation | null>(() => {
  if (!router.options.history.state.back) {
    return null;
  }

  return router.resolve({
    path: router.options.history.state.back
  } as RouteRecordRaw);
});

// Currently there is only an animation between the front- and article pages.
// Therefore we only fade when returning from /article
const canFadeIn = ref<boolean>(previousRoute.value?.name !== "article");
onMounted(() => {
  setTimeout(() => (canFadeIn.value = true), 150);
});

const showPromotion = computed<boolean>(() => {
  if (isIframe()) {
    return false;
  }

  return activeLanguageGroup.value === "german";
});

const lgPaddingYClass = computed<string>(() => {
  if (showPromotion.value) {
    return "py-lg-3";
  }

  return "py-lg-0";
});

const trackInflectionSchemeToolNewsClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcNews,
    MatomoEventAction.ClickInflectionSchemeToolNews
  );
};
</script>

<template>
  <main class="container home px-0 py-2" :class="lgPaddingYClass">
    <section v-if="showPromotion" class="home__large-promotion">
      <Transition name="fade-in">
        <div v-if="canFadeIn">
          <InflectionSchemeToolPromotion @click-cta="trackInflectionSchemeToolNewsClick()" />
        </div>
      </Transition>
    </section>

    <section class="home__search-bar-container">
      <SearchBar scroll-target="dictionary-controls" />
      <SearchBarBanner class="d-none" />
    </section>

    <section v-if="showPromotion" class="d-lg-none m-5">
      <Transition name="fade-in">
        <div v-if="canFadeIn"></div>
      </Transition>
    </section>
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.home {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 1fr;
  justify-content: center;

  #media-breakpoint-up(lg, {
    grid-template-rows: 1fr 53%;
    row-gap: 50px;
  });

  &__large-promotion {
    grid-row: 2;
    text-align: center;

    #media-breakpoint-up(lg, {
      grid-row: 1;
      display: flex;
      flex-direction: column;
      justify-self: center;
      align-self: end;

      @media (max-height: 570px) {
        display: none ;
      }
    });
  }

  &__search-bar-container {
    align-self: start;
    margin-top: 6px;

    #media-breakpoint-up(lg, {
      margin-top: 0;
      grid-row: 2;
    });
  }
}
</style>
